import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import cimfoniLogo from '@/assets/cimfoni_icon_small.png';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import PasswordStrengthIndicator, { isPasswordValid } from '@/components/PasswordStrengthIndicator';

interface PasswordCreationProps {
  email: string;
  onComplete: () => void;
}

const PasswordCreation: React.FC<PasswordCreationProps> = ({ email, onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const { toast } = useToast();

  React.useEffect(() => {
    const fetchFullName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setFullName(profile.full_name || user.email || '');
        }
      }
    };
    fetchFullName();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid(password)) {
      toast({
        variant: "destructive",
        title: "Weak Password",
        description: "Password must meet all complexity requirements"
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password Mismatch",
        description: "Passwords do not match"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
        data: {
          needs_password_reset: false
        }
      });

      if (error) throw error;

      toast({
        title: "Password Created",
        description: "Your password has been set successfully"
      });

      onComplete();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to create password'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-primary rounded-2xl shadow-lg">
              <img src={cimfoniLogo} alt="Cimfoni" className="h-8 w-8 object-contain" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Operations</h1>
            <p className="text-muted-foreground">Save a Life Agency</p>
          </div>
        </div>

        <Card className="shadow-xl border-0 bg-card/95 backdrop-blur">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">Create Your Password</h3>
                <p className="text-sm text-muted-foreground">
                  First time login for {fullName || email}
                </p>
                <p className="text-xs text-muted-foreground mt-2 p-3 bg-muted rounded-md">
                  Please contact your administrator if you need assistance. Only Super Admin can reset passwords.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input 
                    id="new-password" 
                    type="password" 
                    placeholder="Create a password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    disabled={isLoading}
                  />
                  <PasswordStrengthIndicator password={password} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input 
                    id="confirm-password" 
                    type="password" 
                    placeholder="Confirm your password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    required 
                    disabled={isLoading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading || !isPasswordValid(password)}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Password
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs font-light text-muted-foreground">Mitch Solutions</p>
        </div>
      </div>
    </div>
  );
};

export default PasswordCreation;
